import type { AppQueryBuilderCompileRequest } from "@/features/app-studio/api/appQueryBuilderApi";
import type { AppDrilldownItemType } from "@/features/app-studio/api/appKpisApi";
import type { AppWidgetInputMode } from "@/features/app-studio/lib/appChartLayout";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

/** One displayed column: the source key plus an optional display-name override. */
export type AppTableColumnConfig = {
  key: string;
  label?: string | null;
};

export type AppTableSortConfig = {
  key: string;
  direction: "asc" | "desc";
};

/**
 * Presentation config layered on the query result at render time (never changes the
 * query): which columns show (ordered), header-name overrides, and an optional sort.
 */
export type AppTableDisplayConfig = {
  columns: AppTableColumnConfig[];
  sort?: AppTableSortConfig | null;
};

/**
 * One clickable column of a table: clicking a cell in `trigger_column` opens widget
 * `(item_type, item_id)` filtered by that row's `value_column` value.
 */
export type AppTableDrilldown = {
  id: number;
  trigger_column: string;
  value_column: string;
  /**
   * The semantic dimension `value_column` resolved to on save, which is how the server filters the
   * target. Read-only and server-derived. `null` means it matched no dimension, so the filter falls
   * back to matching the target query's own output columns — narrower, since it can then only reach
   * a column the target actually selects.
   */
  dimension_name?: string | null;
  item_type: AppDrilldownItemType;
  item_id: number;
};

export type AppTableSummary = {
  id: number;
  app_id: number;
  /** The table's SQL lives on its referenced AppQuery; not returned on the summary. */
  sql?: string | null;
  /** Reference to the reusable AppQuery that supplies this table's SQL. */
  query_id?: number | null;
  description: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span: number;
  sort_order: number;
  tab_id?: number | null;
  input_mode: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** Configured clickable columns, so the config UI needs no second round trip. */
  drilldowns?: AppTableDrilldown[];
  created_at: string;
  updated_at: string;
};

export type AppTableDataItem = {
  id: number;
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span: number;
  sort_order: number;
  tab_id?: number | null;
  columns: string[];
  rows: Record<string, unknown>[];
  error?: string | null;
  /** Display names of active tab filters that did not apply to this widget (drives the badge). */
  ignored_filters?: string[] | null;
  /**
   * True when this widget is some KPI card's drill-down widget: it is excluded from the tab
   * grid and only rendered inside the drill-down dialog.
   */
  is_drilldown_target?: boolean;
  /**
   * Clickable columns of this table. Present on the data item because the runtime views render
   * cells straight from this payload and never fetch the summary.
   */
  drilldowns?: AppTableDrilldown[];
  /**
   * The stored AI summary. `undefined` on the published stream's `item` frame, where it arrives in a
   * later `item_summary` frame; `null` means either not generated yet, or generated and found
   * unsummarisable (an errored widget or one with no rows).
   */
  summary?: string | null;
};

export type AppTablesDataResponse = {
  tables: AppTableDataItem[];
};

function isAppTableSummary(value: unknown): value is AppTableSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    (row.sql === null || row.sql === undefined || typeof row.sql === "string") &&
    (row.query_id === undefined || row.query_id === null || typeof row.query_id === "number") &&
    (row.description === null || typeof row.description === "string") &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    (row.input_mode === undefined || typeof row.input_mode === "string") &&
    (row.input_config === undefined ||
      row.input_config === null ||
      typeof row.input_config === "object") &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppTableDataItem(value: unknown): value is AppTableDataItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    Array.isArray(row.columns) &&
    Array.isArray(row.rows) &&
    (row.error === undefined || row.error === null || typeof row.error === "string")
  );
}

export async function fetchAppTables(appId: number): Promise<AppTableSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/tables`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppTableSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function fetchAppTablesData(appId: number): Promise<AppTableDataItem[]> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/data`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  const tables = (data as AppTablesDataResponse).tables;
  if (!Array.isArray(tables) || !tables.every(isAppTableDataItem)) {
    throw new Error("Unexpected response shape");
  }
  return tables;
}

export type AddTableToAppPayload = {
  /** Provide exactly one of `sql` (inline) or `query_id` (reuse a saved query). */
  sql?: string;
  query_id?: number | null;
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
};

export type UpdateAppTablePayload = {
  sql?: string;
  query_id?: number | null;
  description?: string | null;
  display_config?: AppTableDisplayConfig | null;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
};

export async function addTableToApp(
  appId: number,
  payload: AddTableToAppPayload,
): Promise<AppTableSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/tables`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppTable(
  appId: number,
  tableId: number,
  payload: UpdateAppTablePayload,
): Promise<AppTableSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppTable(appId: number, tableId: number): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}

function isAppTableDrilldown(value: unknown): value is AppTableDrilldown {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.trigger_column === "string" &&
    typeof row.value_column === "string" &&
    // Optional, and null when the filter column matched no dimension.
    (row.dimension_name === undefined ||
      row.dimension_name === null ||
      typeof row.dimension_name === "string") &&
    (row.item_type === "chart" || row.item_type === "table") &&
    typeof row.item_id === "number"
  );
}

export type AppTableDrilldownPayload = {
  trigger_column: string;
  value_column: string;
  item_type: AppDrilldownItemType;
  item_id: number;
};

export async function fetchAppTableDrilldowns(
  appId: number,
  tableId: number,
): Promise<AppTableDrilldown[]> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}/drilldowns`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppTableDrilldown)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function addAppTableDrilldown(
  appId: number,
  tableId: number,
  payload: AppTableDrilldownPayload,
): Promise<AppTableDrilldown> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}/drilldowns`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableDrilldown(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppTableDrilldown(
  appId: number,
  tableId: number,
  drilldownId: number,
  payload: AppTableDrilldownPayload,
): Promise<AppTableDrilldown> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}/drilldowns/${drilldownId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppTableDrilldown(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppTableDrilldown(
  appId: number,
  tableId: number,
  drilldownId: number,
): Promise<void> {
  const res = await apiFetch(`/v1/apps/${appId}/tables/${tableId}/drilldowns/${drilldownId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
